import {
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import type { ICalendarContext } from "./types";
import { TimePeriod } from "./types";
import { CalendarContext } from "./context";
import { clsx } from "clsx";

export const Years = () => {
  const {
    handleTimePeriodChange,
    value,
    onChange,
    onDateForPeriodChange,
    range,
    innerRange,
    currentInnerRangePointer,
    min,
    max,
  }: ICalendarContext = useContext(CalendarContext);
  const scrollToYearRef = useRef<HTMLButtonElement>(null);

  const yearsRange: number[] = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const minYear = min ? min.getFullYear() : currentYear - 99;
    const maxYear = max ? max.getFullYear() : currentYear + 99;
    const result: number[] = [];

    for (let i = minYear; i <= maxYear; i++) {
      result.push(i);
    }

    return result;
  }, [min, max]);

  const onYearClick = useCallback(
    (year: number) => (): void => {
      if (range) {
        const currentInnerRangeValue =
          innerRange.current[currentInnerRangePointer.current];

        if (!currentInnerRangeValue) {
          innerRange.current.push(new Date());
        }

        const nextValue: Date =
          innerRange.current[innerRange.current.length - 1] || new Date();
        nextValue.setFullYear(year);
        onDateForPeriodChange(nextValue);
        handleTimePeriodChange(TimePeriod.Months);
      } else {
        const nextValue: Date = value ? new Date(value) : new Date();
        nextValue.setFullYear(year);
        onChange(nextValue);
        onDateForPeriodChange(nextValue);
        handleTimePeriodChange(TimePeriod.Months);
      }
    },
    [
      range,
      innerRange,
      currentInnerRangePointer,
      onDateForPeriodChange,
      handleTimePeriodChange,
      value,
      onChange,
    ],
  );

  const yearsJSX = yearsRange.map((year: number, index: number) => {
    const today: Date = new Date();
    const isCurrentYear: boolean = today.getFullYear() === year;
    const isSelectedYear = range
      ? false
      : !!value && value.getFullYear() === year;

    const refCondition =
      isSelectedYear ||
      isCurrentYear ||
      (!value && index === yearsRange.length - 1);
    const ref = refCondition ? scrollToYearRef : null;

    const rootClass: string = clsx(
      "nuco-calendar__dateButton nuco-calendar__dateButton--current nuco-calendar__year",
      isCurrentYear && "nuco-calendar__dateButton--today",
      isSelectedYear && "nuco-calendar__dateButton--selected",
    );

    return (
      <button
        key={year}
        ref={ref}
        className={rootClass}
        onClick={onYearClick(year)}
      >
        {year}
      </button>
    );
  });

  useLayoutEffect(() => {
    if (!scrollToYearRef.current) {
      return;
    }

    scrollToYearRef.current.scrollIntoView();
  }, []);

  return (
    <div className="nuco-calendar__years-wrapper">
      <div className="nuco-calendar__dates nuco-typography--button-md">
        {yearsJSX}
      </div>
    </div>
  );
};
